<template>
  <b-nav-item-dropdown
    ref="dropdown"
    class="dropdown-notification mr-25 custom-notification-dropdown"
    menu-class="dropdown-menu-media notification-menu"
    right
    @show="handleDropdownShow"
  >
    <template #button-content>
      <feather-icon
        :badge="unreadBadge"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <li class="dropdown-menu-header">
      <div class="dropdown-header p-1">
        <div>
          <h4 class="notification-title mb-0">Notifications</h4>
          <small class="notification-summary">{{ unreadSummary }}</small>
        </div>

        <b-link
          v-if="unreadCount"
          class="mark-all-link"
          @click.stop.prevent="markAllRead"
        >
          Mark all read
        </b-link>
      </div>
    </li>

    <div class="notification-list">
      <div v-if="isLoading" class="notification-state">
        <b-spinner small label="Loading notifications..." />
        <span>Loading notifications...</span>
      </div>

      <div v-else-if="loadError" class="notification-state notification-state--error">
        <span>{{ loadError }}</span>
        <b-link @click.stop.prevent="fetchNotifications(true)">
          Retry
        </b-link>
      </div>

      <div v-else-if="!notifications.length" class="notification-state">
        No notifications yet.
      </div>

      <template v-else>
        <button
          v-for="item in notifications"
          :key="item.id"
          type="button"
          class="notification-item"
          :class="{ 'notification-item--unread': !item.isRead }"
          @click="handleNotificationClick(item)"
        >
          <div class="notification-item__body">
            <NotificationAvatar
              :image="item.image"
              :initials="item.initials"
              :alt="item.title"
              :size="36"
            />

            <div class="ml-1 notification-copy">
              <div class="notification-head">
                <h6 class="mb-0 notification-name">{{ item.title }}</h6>
                <span v-if="!item.isRead" class="notification-dot" />
              </div>
              <p class="mb-0 notification-subtitle">{{ item.message || 'No message provided.' }}</p>
              <small class="notification-time">{{ item.timeAgo || item.createdAt }}</small>
            </div>
          </div>

          <small v-if="pendingIds[item.id]" class="notification-pending">Saving...</small>
        </button>
      </template>
    </div>

    <li class="dropdown-menu-footer">
      <b-link class="view-all-link" @click="viewAllNotifications">
        View all notifications
      </b-link>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BLink,
  BNavItemDropdown,
  BSpinner,
} from 'bootstrap-vue'
import NotificationAvatar from '@/components/admin/NotificationAvatar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  ADMIN_NOTIFICATIONS_UPDATED,
  normalizeNotificationsResponse,
  resolveAdminNotificationRoute,
} from '@/utils/adminNotifications'
import { notifyError } from '@/utils/uiActions'

export default {
  components: {
    BLink,
    BNavItemDropdown,
    BSpinner,
    NotificationAvatar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      notifications: [],
      unreadCount: 0,
      pendingIds: {},
      pollTimerId: null,
    }
  },
  computed: {
    unreadBadge() {
      if (!this.unreadCount) return null
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
    },
    unreadSummary() {
      if (!this.unreadCount) return 'All caught up'
      return `${this.unreadCount} unread`
    },
  },
  created() {
    this.fetchNotifications()
    this.pollTimerId = window.setInterval(() => {
      this.fetchUnreadOnly()
    }, 60000)
    this.$root.$on(ADMIN_NOTIFICATIONS_UPDATED, this.handleNotificationsUpdated)
  },
  beforeDestroy() {
    if (this.pollTimerId) {
      window.clearInterval(this.pollTimerId)
    }
    this.$root.$off(ADMIN_NOTIFICATIONS_UPDATED, this.handleNotificationsUpdated)
  },
  methods: {
    handleNotificationsUpdated(payload = {}) {
      if (typeof payload.unreadCount === 'number') {
        this.unreadCount = payload.unreadCount
      }

      if (this.$route.name === 'notification') return

      this.fetchNotifications(true)
    },
    async handleDropdownShow() {
      await this.fetchNotifications(true)
    },
    async fetchNotifications(force = false) {
      if (this.isLoading && !force) return

      this.isLoading = true
      this.loadError = ''

      try {
        const response = await adminPanelApi.notifications({ view: 'dropdown' })
        const normalized = normalizeNotificationsResponse(response.data || {})

        this.notifications = normalized.notifications
        this.unreadCount = normalized.unreadCount
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Unable to load notifications.'
      } finally {
        this.isLoading = false
      }
    },
    async fetchUnreadOnly() {
      try {
        const response = await adminPanelApi.notifications({ view: 'dropdown' })
        const normalized = normalizeNotificationsResponse(response.data || {})
        this.unreadCount = normalized.unreadCount
      } catch (error) {
        // Keep existing badge on polling failure.
      }
    },
    async handleNotificationClick(item) {
      await this.markNotificationRead(item)

      const route = resolveAdminNotificationRoute(item)
      if (route) {
        this.$refs.dropdown.hide()
        this.$router.push(route)
      }
    },
    async markNotificationRead(item) {
      if (!item?.id || item.isRead || this.pendingIds[item.id]) return

      this.$set(this.pendingIds, item.id, true)

      try {
        await adminPanelApi.notificationsMarkRead({ notification_ids: [item.id] })

        this.notifications = this.notifications.map(notification => (
          notification.id === item.id
            ? { ...notification, isRead: true }
            : notification
        ))
        this.unreadCount = Math.max(0, this.unreadCount - 1)
        this.$root.$emit(ADMIN_NOTIFICATIONS_UPDATED, { unreadCount: this.unreadCount })
      } catch (error) {
        notifyError(this, error.response?.data?.message || 'Unable to update the notification.')
      } finally {
        this.$delete(this.pendingIds, item.id)
      }
    },
    async markAllRead() {
      if (!this.unreadCount) return

      try {
        await adminPanelApi.notificationsMarkRead({ mark_all: true })

        this.notifications = this.notifications.map(item => ({
          ...item,
          isRead: true,
        }))
        this.unreadCount = 0
        this.$root.$emit(ADMIN_NOTIFICATIONS_UPDATED, { unreadCount: 0 })
      } catch (error) {
        notifyError(this, error.response?.data?.message || 'Unable to mark notifications as read.')
      }
    },
    viewAllNotifications() {
      this.$refs.dropdown.hide()
      this.$router.push({ name: 'notification' })
    },
  },
}
</script>

<style lang="scss">
.custom-notification-dropdown {
  .notification-menu {
    width: 360px;
    padding: 0;
    border-radius: 0.5rem;
  }

  .dropdown-menu-header {
    border-bottom: 1px solid #ebe9f1;
  }

  .dropdown-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .notification-title {
    font-size: 1.1rem;
    color: #2f2b3d;
    font-weight: 600;
  }

  .notification-summary {
    color: #6e6b7b;
    font-size: 0.8rem;
  }

  .mark-all-link {
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .notification-list {
    max-height: 420px;
    overflow-y: auto;
  }

  .notification-state {
    padding: 1rem;
    color: #6e6b7b;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    text-align: center;
  }

  .notification-state--error {
    flex-direction: column;
  }

  .notification-item {
    width: 100%;
    border: 0;
    background: transparent;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid #ebe9f1;
    text-align: left;
    transition: background-color 0.2s ease;
  }

  .notification-item:hover {
    background: #f8f8fb;
  }

  .notification-item--unread {
    background: rgba(115, 103, 240, 0.04);
  }

  .notification-item__body {
    display: flex;
    align-items: flex-start;
  }

  .notification-copy {
    min-width: 0;
    flex: 1;
  }

  .notification-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .notification-name {
    color: #2f2b3d;
    font-size: 0.94rem;
    font-weight: 600;
  }

  .notification-subtitle {
    color: #6e6b7b;
    font-size: 0.86rem;
    line-height: 1.35;
    margin-top: 0.25rem;
  }

  .notification-time,
  .notification-pending {
    color: #b9b9c3;
    font-size: 0.76rem;
  }

  .notification-pending {
    display: block;
    margin-top: 0.45rem;
    text-align: right;
  }

  .notification-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    background: #011425;
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  .dropdown-menu-footer {
    border-top: 1px solid #ebe9f1;
    padding: 0.75rem 1rem;
    text-align: center;
  }

  .view-all-link {
    color: #2f2b3d !important;
    font-weight: 500;
  }
}
</style>
